import React from 'react'
import { Button, Icon, Message } from 'semantic-ui-react'
import { useQuery } from '@cbgms/base/http/use-query'
import { useDispatch, useSelector } from 'react-redux'
import { ClockingType } from '@cbgms/api/modules/clocking-actions'
import { clockInAction, clockOutAction, currentClockingAction, setClockingActions } from './state/actions'
import { useTranslation } from 'react-i18next'
import { listClockingItems } from 'app/settings/components/ClockingItems/actions'

import { getWorkordersAssignedToEmployee } from 'app/workorder/state/workorder/actions'
import TimeActionsOverview from './TimeActionsOverview'
import { IRootReducerState } from 'state/rootReducer'
import { formatString } from '@cbgms/base/utils/helpers'
import { query } from '@cbgms/components/list'
import { ClockingItemRow, ClockingItemType } from '@cbgms/api/modules/clocking-items'
import { IWorkorderResponse } from '@cbgms/api'
import moment from 'moment'
import { apiFormat } from '@cbgms/base/utils/datetime'
import { Loader } from '@carsys/ui'
import { useFeature } from '@cbgms/base/feature-flags'

export interface ITimeRegistrationProps {}

const TimeRegistration: React.FC<ITimeRegistrationProps> = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const employeeUUID = useSelector<IRootReducerState, string>(state => state.auth.employee.UUID)

  const hasAllOrdersinTimeRegistration = useFeature('orders-in-time-registration')

  const {
    data: result,
    isLoading,
    refetch: reload
  } = useQuery(
    ['TimeRegistration'],
    async () => {
      const currentResponse = await dispatch(currentClockingAction())

      const shopID = currentResponse.Data.Day ? currentResponse.Data.Day.ShopID : undefined

      try {
        const clockingItemsPromise = dispatch(
          listClockingItems(
            {
              fields: 'ClockingItem.*',
              search: query<ClockingItemRow>()
                .equals(x => x.ClockingItem.IsActive, true)
                .and()
                .equals(x => x.ClockingItem.ClockingItemType, ClockingItemType.Generic)
                .toString()
            },
            shopID
          )
        )

        const workorderClockingItemsPromise = dispatch(
          listClockingItems(
            {
              fields: 'ClockingItem.*',
              search: query<ClockingItemRow>()
                .equals(x => x.ClockingItem.IsActive, true)
                .and()
                .equals(x => x.ClockingItem.ClockingItemType, ClockingItemType.WorkOrder)
                .toString()
            },
            shopID
          )
        )

        let workorderItemsPromise

        const startDate = moment().subtract(7, 'days').format(apiFormat.yearMonthDay)
        const endDate = moment().add(7, 'days').format(apiFormat.yearMonthDay)

        if (hasAllOrdersinTimeRegistration) {
          workorderItemsPromise = dispatch(
            getWorkordersAssignedToEmployee(
              {
                search: query<Partial<IWorkorderResponse> & Required<Pick<IWorkorderResponse, 'Appointment'>>>()
                  .greaterThanOrEqual(o => o.Appointment.Date, startDate)
                  .and()
                  .lessThanOrEqual(o => o.Appointment.Date, endDate)
                  .toString()
              },
              shopID
            )
          )
        } else {
          workorderItemsPromise = dispatch(
            getWorkordersAssignedToEmployee(
              {
                search: query<Partial<IWorkorderResponse> & Required<Pick<IWorkorderResponse, 'Appointment'>>>()
                  .equals(o => o.Appointment.Date, moment().format(apiFormat.yearMonthDay))
                  .toString()
              },
              shopID
            )
          )
        }

        const [clockingItemsResponse, workorderItemsResponse, workorderClockingItemsResponse] = await Promise.all([
          clockingItemsPromise,
          workorderItemsPromise,
          workorderClockingItemsPromise
        ])

        dispatch(setClockingActions(currentResponse.Data))

        return {
          currentActions: currentResponse.Data,
          clockingItems: clockingItemsResponse.Data.map(item => item.ClockingItem),
          workorderClockingItems: workorderClockingItemsResponse?.Data.map(item => item.ClockingItem) ?? [],
          workorderItems: workorderItemsResponse.Data.map(item => ({
            Name: item.Vehicle.Registration || formatString(item.Account.FirstName, item.Account.NamePrefix, item.Account.Name),
            UUID: item.Workorder.UUID,
            UrlToPage: `/workorders/${item.Workorder.UUID}`,
            Brand: item.Vehicle.Manufacturer,
            AppointmentDate: item.Appointment?.Date || ''
          }))
        }
      } catch {
        return null
      }
    },
    { cacheTime: 0 }
  )

  /**
   * We are using the state from the store instead of the result directly so we can automatically sync between the dashboard, the top bar
   * and the work order page when we clock in or out
   */
  const currentActions = useSelector((state: IRootReducerState) => state.timeRegistration)

  if (isLoading && !result) {
    return <Loader loading />
  }

  if (!result || !currentActions) {
    return <Message data-testid='timeregistration-error' color='red' content={t('TimeRegistration.ErrorLoading')} />
  }

  const clockIn = async (type: ClockingType, typeUUID?: string, workorderClockingItemUUID?: string) => {
    const isWorkorderType = type === ClockingType.Workorder || type === ClockingType.WorkorderItem
    const clockType = isWorkorderType && workorderClockingItemUUID ? ClockingType.WorkorderItem : type

    const { Data } = await dispatch(
      clockInAction({
        Type: clockType,
        ClockingItemUUID:
          clockType === ClockingType.Item ? typeUUID : clockType === ClockingType.WorkorderItem ? workorderClockingItemUUID : undefined,
        WorkorderUUID: isWorkorderType ? typeUUID : undefined
      })
    )

    /**
     * We want to reload the lists of items when we check in for the day, because we need the shopID from when you checked in for the day
     * to decide what clocking items and work orders to retrieve
     */
    if (type === ClockingType.Day) {
      await reload()
    } else {
      dispatch(setClockingActions(Data))
    }
  }

  const clockOut = async (UUID: string) => {
    const { Data } = await dispatch(clockOutAction({ UUID }))

    dispatch(setClockingActions(Data))
  }

  const startDay = () => clockIn(ClockingType.Day)
  const stopDay = () => currentActions.Day && clockOut(currentActions.Day.UUID)

  const clockoutCurrentAction = () => currentActions.Action && clockOut(currentActions.Action.UUID)

  return (
    <React.Fragment>
      {!currentActions.Day && (
        <Button data-testid='timeregistration-startday' primary labelPosition='left' fluid icon onClick={startDay}>
          <Icon name='play' />
          {t('TimeRegistration.StartDay')}
        </Button>
      )}
      {currentActions.Day && (
        <TimeActionsOverview
          clockingItems={result.clockingItems}
          workorderItems={result.workorderItems}
          workorderClockingItems={result.workorderClockingItems}
          currentAction={currentActions.Action}
          dayAction={currentActions.Day}
          stopDay={stopDay}
          clockIn={clockIn}
          clockOut={clockoutCurrentAction}
          employeeUUID={employeeUUID}
        />
      )}
    </React.Fragment>
  )
}

export default TimeRegistration
